/**
 * Converts an HTML string to Markdown.
 *
 * Lazy-imports `turndown` and the GitHub Flavored Markdown plugin so they are
 * pulled into the bundle only when a Markdown conversion is actually needed.
 *
 * @param {String} html HTML markup to convert.
 * @param {Object} [options]
 * @param {Boolean} [options.keepReferenceAnchors] - Keeps the reference anchors `<a data-type data-id>`, `<u>` and
 *   highlighted text as inline HTML, which Markdown has no syntax for (the AI Helper's prompt).
 * @returns {Promise<String>} The Markdown representation (empty string for empty input).
 */
export async function htmlToMarkdown(html, options) {
  if (!html) {
    return '';
  }
  await loadHtmlToMarkdown();
  return convert(html, options);
}

let converterModules;

/** Loads turndown ahead, for `htmlToMarkdownNow`. */
export const loadHtmlToMarkdown = async () => (converterModules ||= await Promise.all([import('turndown'), import('turndown-plugin-gfm')]));

/** As `htmlToMarkdown`, at once, for a copy, which cannot wait; `null` until `loadHtmlToMarkdown` is done. */
export const htmlToMarkdownNow = (html, options) => (converterModules ? (html ? convert(html, options) : '') : null);

function convert(html, { keepReferenceAnchors = false } = {}) {
  const [{ default: TurndownService }, { gfm }] = converterModules;
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    emDelimiter: '_',
  });
  turndownService.use(gfm);
  // A user chip is `@Name`, as it was typed; a link whose text is its own URL is that URL.
  turndownService.addRule('userMention', {
    filter: (node) => node.nodeName === 'A' && node.getAttribute('data-type') === 'user',
    replacement: (content) => `@${content}`,
  });
  turndownService.addRule('bareUrl', {
    filter: (node) => node.nodeName === 'A' && !!node.getAttribute('href') && node.textContent === node.getAttribute('href'),
    replacement: (content, node) => node.getAttribute('href'),
  });
  // `- item` / `1. item`, no blank line between items (turndown pads the marker to 4 and keeps a
  // `<li><p>`'s blank line). A nested list is indented to the item's text, as marked expects.
  turndownService.addRule('listItem', {
    filter: 'li',
    replacement: (content, node) => {
      const list = node.parentNode;
      const prefix = list.nodeName === 'OL' ? `${Number(list.getAttribute('start') || 1) + Array.prototype.indexOf.call(list.children, node)}. ` : '- ';
      const text = content.replace(/^\n+|\n+$/g, '').replace(/\n\s*\n/g, '\n').replace(/\n/g, `\n${' '.repeat(prefix.length)}`);
      return prefix + text + (node.nextSibling ? '\n' : '');
    },
  });
  // The editor writes a code block as a plain `<pre>`, which turndown fences only with a `<code>` inside. The fence
  // is longer than any run of backticks in the code, so a ``` inside it does not end the block.
  turndownService.addRule('plainPre', {
    filter: (node) => node.nodeName === 'PRE' && node.firstChild?.nodeName !== 'CODE',
    replacement: (content, node) => {
      const code = node.textContent.replace(/\n$/, '');
      const fence = '`'.repeat(Math.max(3, ...(code.match(/`+/g) || []).map((run) => run.length + 1)));
      return `\n\n${fence}\n${code}\n${fence}\n\n`;
    },
  });
  if (keepReferenceAnchors) {
    // Marked reads inline HTML, so typed text that looks like a tag or an entity must stay text.
    const escapeMarkdown = turndownService.escape.bind(turndownService);
    turndownService.escape = (text) => escapeMarkdown(text).replace(/&(?=#?\w+;)/g, '&amp;').replace(/</g, '&lt;');
    // A rule, not `keep()`: the link rule would otherwise turn an anchor with an `href` into `[title](href)`.
    turndownService.addRule('inlineHtml', {
      filter: (node) =>
        (node.nodeName === 'A' && node.hasAttribute('data-type') && node.hasAttribute('data-id')) ||
        node.nodeName === 'U' ||
        (node.nodeName === 'SPAN' && /background-color/i.test(node.getAttribute('style') || '')),
      replacement: (content, node) => node.outerHTML,
    });
  }

  // An empty line (`<div><br></div>`) is written as `  `; it is a plain blank line, and a run of them is one.
  return turndownService.turndown(html).replace(/^[ \t\u00a0]+$/gm, '').replace(/\n{3,}/g, '\n\n').trim();
}
